// TheSportsDB is a free, crowd-sourced sports database explicitly offered
// for displaying real team/league artwork in fan-made and hobby apps —
// exactly this use case. See thesportsdb.com/documentation.
//
// The shared '123' key below is TheSportsDB's public test key: fine for
// getting this running immediately, but it's heavily rate-limited and its
// team *search* endpoint is restricted to a handful of well-known teams for
// non-personal keys. For reliable coverage of every team in your app, sign
// up free at thesportsdb.com, grab your personal key, and set it as
// VITE_SPORTSDB_KEY — no code changes needed.
const SPORTSDB_KEY = import.meta.env.VITE_SPORTSDB_KEY ?? "123";
const BASE = `https://www.thesportsdb.com/api/v1/json/${SPORTSDB_KEY}`;

// TheSportsDB league ids for the competitions we can currently resolve real
// badges for, verified against TheSportsDB's own league registry. Anything
// not listed here is skipped with zero network requests and just keeps its
// generated badge — safe by default, not broken; TheSportsDB's older
// dataset simply doesn't cover every competition (e.g. Saudi Pro League,
// UEFA Europa Conference League, most non-European domestic cups). Extend
// this as you confirm more ids via
// thesportsdb.com/api/v1/json/123/all_leagues.php.
const KNOWN_LEAGUE_IDS: Record<string, string> = {
  // Domestic leagues
  epl: "4328",
  laliga: "4335",
  seriea: "4332",
  bundesliga: "4331",
  ligue1: "4334",
  eredivisie: "4337",
  primeiraliga: "4344",
  scottishprem: "4330",
  mls: "4346",
  brasileirao: "4351",
  argentinaliga: "4406",
  superlig: "4339",
  belgianpro: "4338",
  chinesesuperleague: "4359",
  aleague: "4356",
  // Domestic cups
  facup: "4482",
  copadelrey: "4483",
  coppaitalia: "4506",
  dfbpokal: "4485",
  coupedefrance: "4484",
  tacadeportugal: "4510",
  // Super cups
  spanishsupercup: "4511",
  supercoppaitaliana: "4507",
  // Continental club
  ucl: "4480",
  uel: "4481",
  libertadores: "4501",
  clubworldcup: "4503",
  // International
  worldcup: "4429",
  euros: "4502",
  copaamerica: "4499",
  afcon: "4496",
  nationsleague: "4490",
};

const teamCache = new Map<string, Promise<string | null>>();
const leagueCache = new Map<string, Promise<string | null>>();

async function safeFetchJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    // Network failure, CORS issue, rate limit, whatever — a badge lookup is
    // strictly cosmetic, so it fails silently rather than surfacing an
    // error anywhere in the UI.
    return null;
  }
}

interface SearchTeamsResponse {
  teams: { strBadge: string | null }[] | null;
}
interface LookupLeagueResponse {
  leagues: { strBadge: string | null }[] | null;
}

/**
 * Looks up a real team crest by name. Results (including misses) are
 * cached per name for the life of the session, so the same team appearing
 * in many match cards only ever triggers one request.
 */
export function resolveTeamBadge(teamName: string): Promise<string | null> {
  const key = teamName.trim().toLowerCase();
  if (!teamCache.has(key)) {
    const promise = safeFetchJson<SearchTeamsResponse>(
      `${BASE}/searchteams.php?t=${encodeURIComponent(teamName)}`
    ).then((data) => data?.teams?.[0]?.strBadge ?? null);
    teamCache.set(key, promise);
  }
  return teamCache.get(key)!;
}

/**
 * Looks up a real competition badge using our internal league catalog id
 * (e.g. "epl", "laliga" from src/data/leagues.ts), mapped to TheSportsDB's
 * own id via KNOWN_LEAGUE_IDS. Returns null immediately (no request) for
 * any league not in that map.
 */
export function resolveLeagueBadge(leagueCatalogId: string): Promise<string | null> {
  const sportsDbId = KNOWN_LEAGUE_IDS[leagueCatalogId];
  if (!sportsDbId) return Promise.resolve(null);

  if (!leagueCache.has(sportsDbId)) {
    const promise = safeFetchJson<LookupLeagueResponse>(`${BASE}/lookupleague.php?id=${sportsDbId}`).then(
      (data) => data?.leagues?.[0]?.strBadge ?? null
    );
    leagueCache.set(sportsDbId, promise);
  }
  return leagueCache.get(sportsDbId)!;
}
